/**
 * Who the firm has in front of one court (Lloyd, 16 Sep 2026).
 *
 * "A section on the court page that lists the clients we have at that court, with their name,
 * citation number and date of birth" — so whoever is covering that courtroom has the day in front of
 * them instead of searching case by case.
 *
 * 🔴 **It says what it cannot see.** Only 2,153 of 5,953 active cases are attached to a court, so
 * this list is complete for the cases that have one and silent about the rest. A docket that looks
 * complete and is not is worse than no docket, so the counts of missing fields are shown rather than
 * left for somebody to discover in a courtroom.
 *
 * Soonest hearing first, undated last. An unknown date is not an urgent one.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CourtApi, { CourtClientsDto } from '../../api/CourtApi';
import { formatDateOnly } from '../../utils/formatDateOnly';

interface Props {
  Court_guid: string;
}

// Through formatDateOnly, not `new Date(...)`: a date-only column parses as UTC midnight and shows
// a day early everywhere west of Greenwich, which is what put a client's date of birth one day out
// on the case page. An em dash rather than its "Not provided" for a table cell.
function formatDay(value?: string | null): string {
  return value ? formatDateOnly(value) : '—';
}

const CourtClientsPanel: React.FC<Props> = ({ Court_guid }) => {
  const [data, setData] = useState<CourtClientsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    CourtApi.getClientsAtCourt(Court_guid)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [Court_guid]);

  const clients = data?.clients ?? [];

  return (
    <Card elevation={3}>
      <CardContent>
        <Typography variant="h6" component="h2">
          Clients at this court ({data?.total ?? 0})
        </Typography>
        <Divider sx={{ my: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : clients.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No active cases are attached to this court.
          </Typography>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Citation number</TableCell>
                  <TableCell>Date of birth</TableCell>
                  <TableCell>Court date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.ticketGuid} hover>
                    <TableCell>{client.clientName || '—'}</TableCell>
                    <TableCell>{client.citationNumber || '—'}</TableCell>
                    <TableCell>{formatDay(client.dateOfBirth)}</TableCell>
                    <TableCell>{formatDay(client.courtDate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {((data?.missingCitation ?? 0) > 0 || (data?.missingDateOfBirth ?? 0) > 0) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                {data?.missingCitation ?? 0} without a citation number,{' '}
                {data?.missingDateOfBirth ?? 0} without a date of birth.
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Only cases attached to this court appear here. A case with no court on its record is
              not listed, whichever court it is actually before.
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CourtClientsPanel;
